import TasksModule from '@modules/TasksModule';
import useTasks from '@modules/TasksModule/hooks/useTasks';
import { serviceState } from '@modules/ServiceModule/workspace/lib/state';
import { Headline } from '@servicetitan/design-system';
import classNames from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { CreateCustomer } from '../CreateCustomer';
import { CustomerSearchProvider } from './context/CustomerSearchProvider';
import { TaskCustomerSearchView } from './TaskCustomerSearchView';
import { SearchParams } from './components/CustomerSearchBox/CustomerSearchBox';
import { CustomerBySearchType } from './data/types';
import getDisplayName from '@lib/utils/getDisplayName';
import maskPhoneNumber from '@lib/utils/maskPhoneNumber';

interface TaskCustomerSearchProps {
  taskId: string;
  taskTenantId: string;
  show?: boolean;
}

/**
 * Renders a customer search component for a task. Should only be used when there is a task with a tenantId.
 * @param taskId - The id of the task
 * @param show - Whether to show the component, should not show if the associated task is not selected.
 * @returns
 */
const TaskCustomerSearch = ({
  taskId,
  show = false,
  taskTenantId,
}: TaskCustomerSearchProps) => {
  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);
  const { taskById } = useTasks();
  const task = taskById[taskId];
  const phoneNumber = task?.from ? maskPhoneNumber(task.from) : undefined;
  const [searchParams, setSearchParams] = useState<SearchParams>({});
  const { userByContactCenterUserId } = useSnapshot(serviceState);

  // Merge searchParams with phone from task (task phone takes precedence)
  const customerProps = {
    ...searchParams,
    ...(phoneNumber && { phone: phoneNumber }),
  };

  const handleSelectCustomer = useCallback(
    (customer?: Partial<CustomerBySearchType>) => {
      if (!task) {
        return;
      }
      if (!customer) {
        return;
      }
      TasksModule.setCustomerOnTask(
        task.id,
        customer.id != null && customer.name != null
          ? { id: customer.id, name: customer.name }
          : undefined,
      );
    },
    [task],
  );

  const handleAddCustomer = () => {
    setCreateCustomerOpen(true);
  };

  const closeAddCustomer = useCallback(() => {
    setCreateCustomerOpen(false);
  }, []);

  const handleSearchParamsChanged = useCallback(
    (params: SearchParams) => {
      if (task?.id) {
        TasksModule.setTaskAttribute(task?.id, 'searchParams', params);
        setSearchParams(params);
      }
    },
    [task?.id],
  );

  const contextValues = useMemo(() => {
    const canCreateCustomer = !!task && task.tenantId !== '';

    return {
      searchParams,
      setSearchParams: handleSearchParamsChanged,
      onAddCustomer: canCreateCustomer ? handleAddCustomer : undefined,
    };
  }, [handleSearchParamsChanged, searchParams, task]);

  // in general we should have a task, and we should never need this.
  if (!task) {
    return null;
  }

  const displayHeading = getDisplayName(
    task.displayName,
    userByContactCenterUserId,
  );

  const tenantId = task.tenantId === '' ? undefined : task.tenantId;

  // we are using 'show' here to toggle the 'display' of the component
  // so it doesn't mount/unmount every time it's selected, because it
  // will lose any state
  return (
    <CustomerSearchProvider
      taskId={taskId}
      tenantId={tenantId}
      {...contextValues}
    >
      <div className={classNames('w-100', { 'd-n': !show })}>
        <Headline size='large' className='m-t-2 m-b-3'>
          {displayHeading}
        </Headline>

        <TaskCustomerSearchView taskId={taskId} tenantId={tenantId} />
      </div>
      {createCustomerOpen && (
        <CreateCustomer
          onClose={closeAddCustomer}
          open={createCustomerOpen}
          onSelectCustomer={handleSelectCustomer}
          tenantId={taskTenantId}
          searchParams={customerProps}
        />
      )}
    </CustomerSearchProvider>
  );
};

export default TaskCustomerSearch;
